import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import '../styles/aws-microsite.css'
import AwsPartnerBadge from '../assets/images/aws-partner-badge.png'
import { awsServices, awsCaseStudies, AWS_CALENDLY_URL } from '../data/awsContent'

// Same-page jumps. Two things rule out the obvious approaches: a HashLink would
// push a second hash onto the HashRouter URL and does not scroll when already on
// this page, and scrollIntoView is swallowed by .app, whose
// `overflow-x: hidden` makes overflow-y compute to `auto`. The document itself
// is the real scroller, so drive window directly.
function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  scrollElementToTop(el, 20);
}

// Whatever is pinned across the top of the viewport, so a scroll target does not
// land underneath it. Measured rather than hard-coded because the navbar is a
// different height on this route and changes again at its own breakpoint.
function pinnedHeaderDepth() {
  let depth = 0;
  for (const el of document.querySelectorAll('nav, header, .navbar')) {
    const position = window.getComputedStyle(el).position;
    if (position !== 'fixed' && position !== 'sticky') continue;
    const rect = el.getBoundingClientRect();
    if (rect.top <= 1 && rect.bottom > depth) depth = rect.bottom;
  }
  return depth;
}

function scrollElementToTop(el, gap) {
  const top = el.getBoundingClientRect().top + window.scrollY - pinnedHeaderDepth() - gap;
  // 'instant', not 'auto': html carries `scroll-behavior: smooth`, and 'auto'
  // defers to it, so reduced-motion readers would still get the animation.
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'instant' : 'smooth' });
}

// The visual half of a story feature. Swapping the image is a one-line change in
// awsContent.js: set `image` to an imported file, or drop the field entirely and
// this renders a labelled placeholder in the same 16:10 box.
function StoryMedia({ story, onActivate, expanded }) {
  return (
    <button
      type="button"
      className="aws-story-media"
      onClick={onActivate}
      aria-expanded={expanded}
      aria-controls={`story-full-${story.id}`}
      aria-label={`${expanded ? 'Close' : 'Read'} the ${story.name} story`}
    >
      {story.image ? (
        <img src={story.image.src} alt={story.image.alt} loading="lazy" />
      ) : (
        <span className="aws-story-placeholder">[ PROJECT IMAGE ]</span>
      )}
    </button>
  );
}

// One block of story prose. The shapes are documented on awsCaseStudies in
// awsContent.js; a plain string is the common case and stays a paragraph.
function StoryBlock({ block }) {
  if (typeof block === 'string') return <p>{block}</p>;
  if (block.lead) return <p className="aws-story-lead">{block.lead}</p>;
  if (block.heading) return <h5 className="aws-story-subhead">{block.heading}</h5>;

  if (block.points) {
    return (
      <ul className="aws-story-points">
        {block.points.map((point, i) =>
          typeof point === 'string' ? (
            <li key={i}>{point}</li>
          ) : (
            <li key={i}>
              {point.text}
              <ul className="aws-story-subpoints">
                {point.sub.map((sub, j) => (
                  <li key={j}>{sub}</li>
                ))}
              </ul>
            </li>
          )
        )}
      </ul>
    );
  }

  if (block.terms) {
    return (
      <dl className="aws-story-terms">
        {block.terms.map((entry) => (
          <div key={entry.term}>
            <dt>{entry.term}</dt>
            <dd>{entry.detail}</dd>
          </div>
        ))}
      </dl>
    );
  }

  if (block.flow) {
    return (
      <p className="aws-story-flow">
        {block.flow.map((step, i) => (
          <React.Fragment key={step}>
            {i > 0 && (
              <span className="aws-story-flow-arrow" aria-hidden="true">
                →
              </span>
            )}
            <span className="aws-story-flow-step">{step}</span>
          </React.Fragment>
        ))}
      </p>
    );
  }

  return null;
}

// The heading sits in its own column so the section label runs down the left
// margin and the prose keeps a readable measure without stranding the right
// third of a 1080px page. Body is wrapped because the two are grid cells.
function StorySection({ title, blocks }) {
  if (!blocks || !blocks.length) return null;
  return (
    <section className="aws-story-block">
      <h4>{title}</h4>
      <div className="aws-story-block-body">
        {blocks.map((block, i) => (
          <StoryBlock key={i} block={block} />
        ))}
      </div>
    </section>
  );
}

// A story either groups its technologies or supplies one flat list. A story
// that names its technology inline supplies neither, and loses the sidebar.
function TechPanels({ story }) {
  const groups = story.technologyGroups
    ? story.technologyGroups
    : [{ title: 'Technologies we used', items: story.technologies }];

  return groups.map((group) => (
    <div key={group.title} className="aws-story-panel">
      <p className="aws-story-panel-title">{group.title}</p>
      <ul className="aws-story-tech">
        {group.items.map((tech) => (
          <li key={tech.name}>
            <span className="aws-story-tech-name">{tech.name}</span>
            {tech.note && <span className="aws-story-tech-note">{tech.note}</span>}
          </li>
        ))}
      </ul>
    </div>
  ));
}

// The full narrative. Kept behind the teaser so the section stays scannable,
// but on the page rather than a separate route, which was the point of the
// brief: the stories live here.
function StoryDetail({ story }) {
  const { engagement, titles = {} } = story;
  const showEngagement = engagement && engagement.confirmed;
  const hasAside = Boolean(story.technologyGroups || story.technologies);

  return (
    <div className="aws-story-detail">
      {showEngagement && (
        <dl className="aws-story-stats">
          <div>
            <dt>Engagement</dt>
            <dd>{engagement.duration}</dd>
          </div>
          <div>
            <dt>Team size</dt>
            <dd>{engagement.teamSize}</dd>
          </div>
        </dl>
      )}

      <div className={'aws-story-columns' + (hasAside ? '' : ' aws-story-columns-wide')}>
        <div className="aws-story-prose">
          <StorySection title={titles.problem || 'Problem'} blocks={story.problem} />
          <StorySection title={titles.about || 'About the project'} blocks={story.about} />
          <StorySection title={titles.solution || 'Solution'} blocks={story.solution} />
          <StorySection title={titles.value || 'Business value'} blocks={story.value} />
          <StorySection title={titles.impact || 'The impact'} blocks={story.impact} />
        </div>

        {hasAside && (
          <aside className="aws-story-aside">
            <TechPanels story={story} />
          </aside>
        )}
      </div>

      {story.valueImage && (
        <figure className="aws-story-wide-figure">
          <img src={story.valueImage.src} alt={story.valueImage.alt} loading="lazy" />
        </figure>
      )}
    </div>
  );
}

function StoryFeature({ story, reversed, open, onToggle }) {
  return (
    <article
      className={
        'aws-story' + (reversed ? ' aws-story-reversed' : '') + (open ? ' aws-story-open' : '')
      }
      id={`story-${story.id}`}
    >
      <div className="aws-story-feature">
        <div className="aws-story-text">
          <p className="aws-story-index">
            <span className="aws-story-order">{story.order}</span>
            <span className="aws-story-discipline">{story.discipline}</span>
          </p>
          <h3 className="aws-story-name">{story.name}</h3>
          <p className="aws-story-kicker">{story.kicker}</p>
          <p className="aws-story-teaser">{story.teaser}</p>
          <button
            type="button"
            className="aws-story-link"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={`story-full-${story.id}`}
          >
            {open ? 'Close story' : 'Read Story'}
            <span className="aws-story-link-arrow" aria-hidden="true">
              →
            </span>
          </button>
        </div>

        <StoryMedia story={story} onActivate={onToggle} expanded={open} />
      </div>

      <div id={`story-full-${story.id}`} hidden={!open}>
        <StoryDetail story={story} />
      </div>
    </article>
  );
}

export default function AWS() {
  const [openService, setOpenService] = useState(null);
  const [openStory, setOpenStory] = useState(null);
  // Set by the toggle, consumed once by the effect below. A ref rather than
  // state so re-renders from anything else never re-trigger a scroll.
  const pendingScroll = useRef(null);

  // The navbar sits outside this component, so route-specific navbar styling
  // has to be flagged on the body rather than scoped under the wrapper.
  useEffect(() => {
    document.body.classList.add('aws-route');
    return () => document.body.classList.remove('aws-route');
  }, []);

  // Opening a story adds a screenful of content below the fold, which leaves
  // the reader with no idea the page grew. Scroll the story itself to the top
  // of the viewport: starting at the panel's own heading rather than at the
  // narrative means the reader can see which story they are now reading.
  // Closing lands in the same place, which is otherwise scrolled far above.
  // useLayoutEffect, not useEffect with a rAF: React has already committed the
  // DOM by the time this runs, so reading the panel's box here forces the
  // layout and gives its final position. Waiting for an animation frame would
  // mean never scrolling at all in a tab that is not painting.
  useLayoutEffect(() => {
    const pendingId = pendingScroll.current;
    if (!pendingId) return;
    pendingScroll.current = null;

    const story = document.getElementById(`story-${pendingId}`);
    if (story) scrollElementToTop(story, 24);
  }, [openStory]);

  const toggleStory = (id) => {
    setOpenStory(openStory === id ? null : id);
    pendingScroll.current = id;
  };

  return (
    <div className="aws-microsite">
      {/* Hero */}
      <section className="aws-hero" id="aws-top">
        <div className="aws-hero-inner">
          <img src={AwsPartnerBadge} alt="AWS Partner - Select Tier Services" className="aws-partner-badge" />
          <h1>
            Cloud is the future.
            <br />
            <span className="aws-accent">AI makes it a force multiplier.</span>
          </h1>
          <p className="aws-hero-sub">
            Jaggulas Consulting pairs consulting strategy with hands-on AWS engineering,
            so your cloud and AI decisions map to business outcomes, not just infrastructure.
          </p>
          <div className="aws-hero-actions">
            <button
              type="button"
              className="aws-btn aws-btn-primary"
              onClick={() => scrollToSection('aws-services')}
            >
              Explore our AWS services
            </button>
            <button
              type="button"
              className="aws-btn aws-btn-ghost"
              onClick={() => scrollToSection('aws-case-studies')}
            >
              See delivered work
            </button>
          </div>
        </div>
      </section>

      {/* Services — each tile expands to its detail */}
      <section className="aws-section" id="aws-services">
        <div className="aws-section-inner">
          <p className="aws-eyebrow">What we do</p>
          <h2>AWS services</h2>
          <div className="aws-service-grid">
            {awsServices.map((s) => {
              const isOpen = openService === s.id;
              return (
                <div key={s.id} className={'aws-service-tile' + (isOpen ? ' open' : '')}>
                  <button
                    type="button"
                    className="aws-service-tile-toggle"
                    aria-expanded={isOpen}
                    aria-controls={s.id}
                    onClick={() => setOpenService(isOpen ? null : s.id)}
                  >
                    <span className="aws-service-tile-name">{s.name}</span>
                    <span className="aws-service-tile-icon" aria-hidden="true">
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>
                  <div id={s.id} className="aws-service-tile-body" hidden={!isOpen}>
                    <ul>
                      {s.points.map((point, i) => (
                        <li key={i}>{point}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Success stories — alternating editorial features, full narrative on demand */}
      <section className="aws-section aws-section-alt aws-stories" id="aws-case-studies">
        <div className="aws-section-inner">
          <p className="aws-eyebrow">Delivered work</p>
          <h2>Success stories</h2>
          <p className="aws-section-intro">
            Three delivered solutions, told as stories: what the constraint was, what we
            built, and what changed commercially.
          </p>

          <div className="aws-story-list">
            {awsCaseStudies.map((story, i) => (
              <StoryFeature
                key={story.id}
                story={story}
                reversed={i % 2 === 1}
                open={openStory === story.id}
                onToggle={() => toggleStory(story.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="aws-cta" id="aws-contact">
        <div className="aws-cta-inner">
          <div>
            <h2>Talk to our Team</h2>
            <p>
              The quickest way to reach us is a short discovery call. Pick a slot that suits
              you and we'll take it from there.
            </p>
          </div>
          <a
            href={AWS_CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="aws-btn aws-btn-primary"
          >
            Book a discovery call
          </a>
        </div>
      </section>
    </div>
  )
}
